import UsersTable from "../UsersTable";
import { getUsers, type AdminUserSummary } from "../../services/UserService";
import { userHasPlusOrHigher, userHasTier, type UserPlanFilter } from "./userPlanFilters";
import { useEffect, useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";

interface UsersTabProps {
  onUserSelect: (userId: string) => void;
}

/** Small pill filter chip with an embedded count. */
function PlanChip({ label, count, dotClass, active, onClick }: {
  label: string; count: number; dotClass: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={count === 0}
      className={`flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors disabled:opacity-35 disabled:cursor-default ${active
        ? "bg-gray-900 dark:bg-white text-white/80 dark:text-dark-900/80 ring-1 ring-gray-900 dark:ring-white"
        : "bg-white dark:bg-dark-800 text-gray-500 dark:text-dark-300 ring-1 ring-gray-200 dark:ring-dark-600 hover:ring-gray-300 dark:hover:ring-dark-500 hover:text-gray-700 dark:hover:text-dark-100"
        }`}
      title={`Filter: ${label}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotClass}`} />
      {label}
      <span className={`font-mono font-bold tabular-nums ${active ? "text-white dark:text-dark-900" : "text-gray-900 dark:text-white"}`}>{count}</span>
    </button>
  );
}

export default function UsersTab({ onUserSelect }: UsersTabProps) {
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [planFilter, setPlanFilter] = useState<UserPlanFilter | null>(null);

  const toggleFilter = (filter: UserPlanFilter) => setPlanFilter((current) => (current === filter ? null : filter));

  useEffect(() => {
    setUsersLoading(true);
    getUsers().then((loadedUsers) => setUsers(Array.isArray(loadedUsers) ? loadedUsers : []))
      .catch((err) => console.error("Error fetching users:", err))
      .finally(() => setUsersLoading(false));
  }, []);

  const facets = useMemo(() => {
    let active = 0, basic = 0, plus = 0, pro = 0, target = 0, dollarGeneral = 0, bestBuy = 0;
    for (const u of users) {
      if (u.retailers?.length) active++;
      if (userHasTier(u, "basic")) basic++;
      if (userHasTier(u, "plus")) plus++;
      if (userHasTier(u, "pro")) pro++;
      const premium = userHasPlusOrHigher(u);
      if (premium && u.targetStoreIds?.length) target++;
      if (premium && u.dollarGeneralStoreIds?.length) dollarGeneral++;
      if (premium && u.bestbuyStoreIds?.length) bestBuy++;
    }
    return { active, basic, plus, pro, target, dollarGeneral, bestBuy };
  }, [users]);

  const exportCSV = () => {
    const header = "email,name,is_active,has_walmart,has_home_depot,created_at";
    const rows = users.map((u) => {
      const active = (u.retailers && u.retailers.length > 0) ? "true" : "false";
      const walmart = u.retailers?.some((r) => r.startsWith("walmart")) ? "true" : "false";
      const homeDepot = u.retailers?.some((r) => r.startsWith("home_depot")) ? "true" : "false";
      const created = u.created ? new Date(u.created).toISOString().split("T")[0] : "";
      const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
      return `${escape(u.userId)},${escape(u.name ?? "")},${active},${walmart},${homeDepot},${created}`;
    });
    const blob = new Blob([header + "\n" + rows.join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(blob),
      download: `dealhawk-users-${new Date().toISOString().slice(0, 10)}.csv`,
    });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const tierChips: { label: string; count: number; dotClass: string; filter: UserPlanFilter }[] = [
    { label: "Basic", count: facets.basic, dotClass: "bg-gray-400", filter: "basic" },
    { label: "Plus", count: facets.plus, dotClass: "bg-violet-500", filter: "plus" },
    { label: "Pro", count: facets.pro, dotClass: "bg-blue-600", filter: "pro" },
  ];

  const premiumChips: { label: string; count: number; dotClass: string; filter: UserPlanFilter }[] = [
    { label: "Target", count: facets.target, dotClass: "bg-red-500", filter: "target" },
    { label: "DG", count: facets.dollarGeneral, dotClass: "bg-yellow-500", filter: "dollar_general" },
    { label: "Best Buy", count: facets.bestBuy, dotClass: "bg-indigo-500", filter: "bestbuy" },
  ];

  return (
    <div className="space-y-4">
      {/* ── Overview: hero count + plan filter chips ─────── */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm px-4 py-3 flex items-center gap-4">
        <button
          type="button"
          onClick={() => toggleFilter("active")}
          disabled={facets.active === 0}
          className={`shrink-0 px-2.5 py-1 -my-1 -mx-1 rounded-xl text-left transition-colors disabled:cursor-default ${planFilter === "active" ? "bg-red-50 dark:bg-red-900/20" : "hover:bg-gray-50 dark:hover:bg-dark-700/60"}`}
          title="Filter: active users"
        >
          <p className={`text-2xl font-mono font-bold tabular-nums leading-tight ${planFilter === "active" ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white"}`}>
            {usersLoading ? "···" : facets.active}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Active users</p>
        </button>

        <div className="w-px self-stretch my-0.5 bg-gray-100 dark:bg-dark-700 shrink-0" />

        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
          {tierChips.map((stat) => (
            <PlanChip
              key={stat.filter}
              label={stat.label}
              count={stat.count}
              dotClass={stat.dotClass}
              active={planFilter === stat.filter}
              onClick={() => toggleFilter(stat.filter)}
            />
          ))}
          <div className="w-px h-5 bg-gray-100 dark:bg-dark-700 mx-1.5 shrink-0" title="Premium retailers, configured by Plus-or-higher users" />
          {premiumChips.map((stat) => (
            <PlanChip
              key={stat.filter}
              label={stat.label}
              count={stat.count}
              dotClass={stat.dotClass}
              active={planFilter === stat.filter}
              onClick={() => toggleFilter(stat.filter)}
            />
          ))}
        </div>

        <button
          onClick={exportCSV}
          disabled={usersLoading}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
          title="Export all users as CSV"
        >
          {usersLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Export CSV
        </button>
      </div>

      <UsersTable users={users} loading={usersLoading} onUserSelect={onUserSelect} planFilter={planFilter} onPlanFilterChange={setPlanFilter} />
    </div>
  );
}
